// Token-fed ECharts options for the ServiceNow Stats panel.
//
// Every other chart in the portal colours itself from `getGrays(isDark)` plus
// hard-coded hex. This module reads the BugZero design tokens off :root
// instead, so charts follow the design system automatically. Values are
// resolved at call time, not import time — callers must rebuild these options
// when the theme changes.

export function tok(name) {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
}

function theme() {
  return {
    ink: tok('--ink'),
    inkSoft: tok('--ink-soft'),
    inkMuted: tok('--ink-muted'),
    line: tok('--line'),
    lineSoft: tok('--line-soft'),
    bg: tok('--bg-elev'),
    prevent: tok('--prevent'),
    plan: tok('--plan'),
    blue2: tok('--bz-blue-2')
  };
}

function baseTooltip(t) {
  return {
    trigger: 'axis',
    padding: [7, 10],
    backgroundColor: t.bg,
    textStyle: { color: t.ink, fontSize: 12 },
    borderColor: t.line,
    borderWidth: 1
  };
}

export function sentChartOption(rows) {
  const t = theme();
  // Past ~4 months the MM-DD labels stop being unambiguous, so show the year.
  // hideOverlap thins them out; no dataZoom needed even at 365 points.
  const long = rows.length > 120;

  return {
    tooltip: baseTooltip(t),
    legend: {
      data: ['Created', 'Updated'],
      bottom: 0,
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: t.inkSoft, fontSize: 12 }
    },
    grid: { left: 8, right: 16, bottom: 34, top: 12, containLabel: true },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: rows.map(r => (long ? r.date : r.date.slice(5))),
      axisLine: { lineStyle: { color: t.line } },
      axisTick: { show: false },
      axisLabel: { color: t.inkMuted, fontSize: 11, hideOverlap: true }
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: t.lineSoft } },
      axisLabel: { color: t.inkMuted, fontSize: 11 }
    },
    series: [
      {
        name: 'Created',
        type: 'line',
        smooth: true,
        symbol: 'none',
        data: rows.map(r => r.created),
        lineStyle: { width: 2, color: t.prevent },
        itemStyle: { color: t.prevent },
        areaStyle: { color: t.prevent, opacity: 0.1 }
      },
      {
        name: 'Updated',
        type: 'line',
        smooth: true,
        symbol: 'none',
        data: rows.map(r => r.updated),
        lineStyle: { width: 2, color: t.blue2 },
        itemStyle: { color: t.blue2 },
        areaStyle: { color: t.blue2, opacity: 0.1 }
      }
    ]
  };
}

export function openChartHeight(barCount) {
  return 30 * barCount + 40;
}

export function openChartOption(rowsAsc) {
  const t = theme();
  return {
    tooltip: { ...baseTooltip(t), axisPointer: { type: 'shadow' } },
    grid: { left: 8, right: 44, bottom: 8, top: 8, containLabel: true },
    xAxis: {
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: t.lineSoft } },
      axisLabel: { color: t.inkMuted, fontSize: 11 }
    },
    yAxis: {
      type: 'category',
      data: rowsAsc.map(r => r.company_name),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: t.inkSoft, fontSize: 12 }
    },
    series: [
      {
        type: 'bar',
        data: rowsAsc.map(r => r.count),
        barMaxWidth: 18,
        itemStyle: { color: t.plan, borderRadius: [0, 3, 3, 0] },
        label: { show: true, position: 'right', color: t.inkMuted, fontSize: 11 }
      }
    ]
  };
}
